import React, { useState } from "react";
import {
  CircularProgress,
  Container,
  TableContainer,
  Table,
  TableHead,
  TableCell,
  TableRow,
  TableBody,
  Typography,
  Button,
  Stack,
  Modal,
} from "@mui/material";

import { makeStyles } from "@mui/styles";

import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

import UploadFile from "../components/UIs/UploadFile";

import { API_URL, backend } from "../../../config";

import readXlsxFile from "read-excel-file";
import { WarningAmber } from "@mui/icons-material";

const useStyles = makeStyles((theme) => ({
  modal: {
    position: "absolute",
    width: 400,
    backgroundColor: "#fff",
    border: "1px solid rgba(0,0,0,.2)",
    borderRadius: ".3rem",
    boxShadow: theme.shadows[5],
    padding: "1rem",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
}));

export const BulkImport = (props) => {
  const classes = useStyles();

  const [errorInfo, setErrorInfo] = useState({ error: false, message: "" });
  const [fileData, setFileData] = useState([]);
  const [fileProcessing, setFileProcessing] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);
  const [loadingFlag, setLoadingFlag] = useState(false);
  const [finishedFlag, setFinishedFlag] = useState(false);

  const handleChangeData = async (data, file) => {
    setFileProcessing(true);
    let dataImported = [];

    switch (file.type) {
      case "text/csv":
        data
          .map((item) => item.data)
          .forEach((item) => {
            if (item.length > 1) {
              dataImported.push(item);
            }
          });

        break;
      case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
        await readXlsxFile(file).then((data) => {
          data.forEach((item) => {
            if (item.length > 1) {
              dataImported.push(item);
            }
          });
        });

        break;
      default:
        setErrorInfo({
          error: true,
          message: "Unsupported file type. Please usload a .csv or .xlsx file.",
        });
    }

    if (!dataImported.length) {
      setErrorInfo({
        error: true,
        message:
          "The imported file couldn't be processed (it didn't generate any data to be imported).",
      });
      return;
    } else {
      const headersIndex = dataImported.findIndex((item) =>
        String(item).toLowerCase().includes("customer name"),
      );

      if (headersIndex === -1) {
        setErrorInfo({
          error: true,
          message:
            "The imported file couldn't be processed (couldn't find the headers from the imported data: customer name).",
        });
        return;
      }

      const headersRow = dataImported[headersIndex].map((item) =>
        String(item || "")
          .trim()
          .toLowerCase(),
      );

      const headersMap = {
        customer_name: headersRow.indexOf("customer name"),
        billing_address: headersRow.indexOf("billing address"),
        billing_city: headersRow.indexOf("billing city"),
        billing_state: headersRow.indexOf("billing state"),
        billing_zip: headersRow.indexOf("billing zip"),
        physical_address: headersRow.indexOf("physical address"),
        physical_city: headersRow.indexOf("physical city"),
        physical_state: headersRow.indexOf("physical state"),
        physical_zip: headersRow.indexOf("physical zip"),
        username: headersRow.indexOf("username"),
        email_address: headersRow.indexOf("email address"),
        password: headersRow.indexOf("password"),
        sale_tax_rate: headersRow.indexOf("sale tax rate"),
        refrigerated_delivery_fee: headersRow.indexOf(
          "refrigerated delivery fee",
        ),
        fuel_surcharge: headersRow.indexOf("fuel surcharge"),
        taxable: headersRow.indexOf("taxable"),
        qb_mapping: headersRow.indexOf("qb mapping"),
        fee: headersRow.indexOf("fee"),
      };

      const rowsMap = dataImported
        .slice(headersIndex + 1)
        .map((row) => ({
          customer_name: String(row[headersMap.customer_name] || "").trim(),
          billing_address: String(row[headersMap.billing_address] || "").trim(),
          billing_city: String(row[headersMap.billing_city] || "").trim(),
          billing_state: String(row[headersMap.billing_state] || "").trim(),
          billing_zip: String(row[headersMap.billing_zip] || "").trim(),
          physical_address: String(
            row[headersMap.physical_address] || "",
          ).trim(),
          physical_city: String(row[headersMap.physical_city] || "").trim(),
          physical_state: String(row[headersMap.physical_state] || "").trim(),
          physical_zip: String(row[headersMap.physical_zip] || "").trim(),
          username: String(row[headersMap.username] || "").trim(),
          email_address: String(row[headersMap.email_address] || "").trim(),
          password: String(row[headersMap.password] || "").trim(),
          sale_tax_rate: +String(row[headersMap.sale_tax_rate] || "").trim(),
          refrigerated_delivery_fee: +String(
            row[headersMap.refrigerated_delivery_fee] || "",
          ).trim(),
          fuel_surcharge: +String(row[headersMap.fuel_surcharge] || "").trim(),
          taxable:
            String(row[headersMap.taxable] || "")
              .trim()
              .toLowerCase() === "true",
          qb_mapping: String(row[headersMap.qb_mapping] || "").trim(),
          fee: +String(row[headersMap.fee] || "").trim(),
        }))
        .map((item) => ({
          ...item,
          valid:
            Boolean(item.customer_name) &&
            Boolean(item.billing_address) &&
            Boolean(item.billing_city) &&
            Boolean(item.billing_zip),
          warning: !Boolean(item.username) || !Boolean(item.password),
        }));

      setFileData(rowsMap);
      setFileProcessing(false);
    }
  };

  const errorFallback = () => {
    setErrorInfo({ error: false, message: "" });
    setFileData([]);
    setFileProcessing(false);
  };

  const toggleConfirmModal = () => setConfirmModal(!confirmModal);

  const confirmModalContent = (
    <div className={classes.modal}>
      {!loadingFlag && !finishedFlag ? (
        <>
          Are you sure you want to bulk import the customer locations? This
          action can't be undone.
          <br />
          <br />
          {fileData.some((item) => item.warning) ? (
            <div style={{ color: "red" }}>
              <b>Note: </b>You have customer locations with missing username or
              password. These will be imported with default credentials.
              <br />
              <br />
            </div>
          ) : (
            <></>
          )}
          <div align="right">
            <Button
              onClick={() => toggleConfirmModal()}
              className={classes.button}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => sendRequest()}
              className={classes.button}
            >
              Confirm
            </Button>
          </div>
        </>
      ) : loadingFlag ? (
        <>
          <div align="center">
            <CircularProgress />
            <div>
              Importing customer locations.
              <br />
              This may take a few minutes depending on the number of customer
              locations being imported.
              <br />
              <br />
              <div style={{ color: "darkblue" }}>
                <b>Note: </b>If there is a major issue while creating any of the
                customer locations, the import process will stop and no customer
                location will be created.
                <br />
                <br />
              </div>
            </div>
          </div>
          {errorInfo.error ? (
            <>
              There was an error while importing customer locations.
              <br />
              <br />
              <span
                style={{
                  color: "red",
                  wordBreak: "break-word",
                  maxHeight: 160,
                  overflow: "hidden",
                  display: "inline-block",
                  whiteSpace: "pre-line",
                  overflowY: "auto",
                }}
              >
                <b>Error:</b>
                <br />
                {typeof errorInfo.message === "object" ? (
                  <>
                    <ul>
                      {Object.keys(errorInfo.message).map((key) => (
                        <li key={key}>
                          <b>{key}:</b>
                          {` ${errorInfo.message[key]}`}
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  errorInfo.message
                )}
                <br />
                <br />
              </span>
              <div align="right">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => resolveError()}
                  className={classes.button}
                >
                  Continue
                </Button>
              </div>
            </>
          ) : (
            <></>
          )}
        </>
      ) : (
        <>
          The customer locations have been imported successfully
          {errorInfo.error ? (
            <>
              <br />
              <br />
              The process completed with soft errors. These didn't prevent the
              import process from completing.
              <br />
              <br />
              <span
                style={{
                  color: "darkblue",
                  wordBreak: "break-word",
                  maxHeight: 160,
                  overflow: "hidden",
                  display: "inline-block",
                  whiteSpace: "pre-line",
                  overflowY: "auto",
                }}
              >
                <b>Important Information:</b>
                <br />
                {typeof errorInfo.message === "object" ? (
                  <>
                    <ul>
                      {Object.keys(errorInfo.message).map((key) => (
                        <li key={key}>
                          <b>{key}:</b>
                          {` ${errorInfo.message[key]}`}
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  errorInfo.message
                )}
              </span>
            </>
          ) : (
            <></>
          )}
          <div align="right">
            <Button
              variant="contained"
              color="primary"
              onClick={() => resolveRequest()}
              className={classes.button}
            >
              Close
            </Button>
          </div>
        </>
      )}
    </div>
  );

  const getErrorMessage = (error) => {
    const errorData = error.response.data;
    let errorMessage = errorData?.message
      ? errorData.message
      : errorData?.detail;

    if (!errorMessage && typeof errorData === "object") {
      const errors = errorData?.errors;

      if (Array.isArray(errors)) {
        errorMessage = errors
          .map((err) => `row: ${err.index} - ${err.error} `)
          .join("\n");
      }
    }

    return errorMessage || error.message;
  };

  const sendRequest = async () => {
    setLoadingFlag(true);

    await backend
      .post(`${API_URL}companylocationsbulkimport/`, fileData)
      .then(({ data }) => {
        if (data?.soft_errors.length) {
          const errorData = {
            response: { data: { errors: data.soft_errors } },
          };

          setErrorInfo({
            error: true,
            message: getErrorMessage(errorData),
          });
        }
        setLoadingFlag(false);
        setFinishedFlag(true);
      })
      .catch((error) => {
        console.log(error);

        setErrorInfo({
          error: true,
          message: getErrorMessage(error),
        });
      });
  };

  const resolveError = () => {
    toggleConfirmModal();
    setLoadingFlag(false);
    setFinishedFlag(false);
    setErrorInfo({ error: false, message: "" });
  };

  const resolveRequest = () => {
    props.history.push("/customers");
  };

  return (
    <Container style={{ minHeight: "80vh" }}>
      <Modal open={confirmModal} onClose={toggleConfirmModal}>
        {confirmModalContent}
      </Modal>
      {!fileData.length && !fileProcessing ? (
        <UploadFile
          {...props}
          errorInfo={errorInfo}
          setErrorInfo={setErrorInfo}
          handleChangeData={handleChangeData}
        />
      ) : fileProcessing ? (
        <div align="center">
          <br />
          <CircularProgress />
          <div>Processing file data</div>
          {errorInfo.error ? (
            <>
              <br />
              <br />
              <div>
                <Typography component="h4" color="error">
                  {errorInfo.message}
                </Typography>
              </div>
              <div align="right">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => errorFallback()}
                >
                  Continue
                </Button>
              </div>
            </>
          ) : (
            <></>
          )}
        </div>
      ) : (
        <></>
      )}
      {!errorInfo.error && fileData.length ? (
        <>
          <Typography component="h6" variant="h6" align="center">
            {`Customer Locations to be Imported: ${fileData.length}`}
          </Typography>
          <TableContainer style={{ maxHeight: "80vh" }}>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  <TableCell size="small">Customer Location Name</TableCell>
                  <TableCell size="small">Address</TableCell>
                  <TableCell size="small">City</TableCell>
                  <TableCell size="small">State</TableCell>
                  <TableCell size="small">Zip Code</TableCell>
                  <TableCell size="small">Valid</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fileData.map((item, index) => (
                  <TableRow key={`row-${index}`} hover tabIndex={-1}>
                    <TableCell size="small">{item.customer_name}</TableCell>
                    <TableCell size="small">{item.billing_address}</TableCell>
                    <TableCell size="small">{item.billing_city}</TableCell>
                    <TableCell size="small">{item.billing_state}</TableCell>
                    <TableCell size="small">{item.billing_zip}</TableCell>
                    <TableCell
                      size="small"
                      style={{
                        background: item.valid ? "lightgreen" : "lightyellow",
                        textAlign: "center",
                      }}
                    >
                      {item.warning ? (
                        <WarningAmber title="Missing username or password" />
                      ) : item.valid ? (
                        <CheckIcon />
                      ) : (
                        <CloseIcon />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Stack
            direction="row"
            justifyContent="right"
            alignItems="center"
            spacing={2}
            mt={2}
          >
            <Button color="primary" onClick={() => errorFallback()}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => toggleConfirmModal()}
              disabled={!fileData.every((item) => item.valid)}
            >
              Import
            </Button>
          </Stack>
        </>
      ) : (
        <></>
      )}
    </Container>
  );
};

export default BulkImport;
